/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppContext } from '../../../../context';
import { Interview, TimeRange } from '../../../../types';
import { getTimeSimple } from '../../../../utils/date-utils';
import React, { useContext, useEffect, useState } from 'react';

import './styles.scss';
import { DateTime, Duration } from 'luxon';
import { timeZoneList } from '../../../../utils/timezones';
import ListTimezones from '../../../../components/ListTimezones';

interface ItineraryCalendarDisplayProps {
    primaryColor: string;
    primaryTextColor: string;
    timeRanges?: TimeRange[];
    interview?: Interview;
}

interface Itinerary {
    dayOfWeek: string;
    dayOfMonth: number;
    monthOfYear: string;
    bookDate: string;
    bookTime: string;
    duration: string;
}

const mapTimeRangeToItinerary = (timeRangeStartDate: DateTime, timeRangeEndDate: DateTime, duration: Duration) => {
    return {
        dayOfWeek: timeRangeStartDate.weekdayShort,
        dayOfMonth: timeRangeStartDate.day,
        monthOfYear: timeRangeStartDate.toFormat('MMM'),
        bookDate: `${timeRangeStartDate.weekdayLong}, ${timeRangeStartDate.toFormat('MMM dd')}`,
        bookTime: `${getTimeSimple(timeRangeStartDate)} - ${getTimeSimple(timeRangeEndDate)}`,
        duration: duration.hours ? `${duration.hours} hr` : `${duration.minutes} min`,
    } as Itinerary;
};

const ItineraryCalendarDisplay: React.FC<ItineraryCalendarDisplayProps> = ({
    primaryColor,
    primaryTextColor,
    timeRanges = [],
    interview,
}) => {
    const { translate } = useContext(AppContext);

    const [selectedTimezone, setSelectedTimezone] = useState<string>(
        interview?.settings?.timeZone ?? DateTime.local().zoneName,
    );
    const newDates = new Map<string, Itinerary[]>();

    const sortedTimeRanges = [...timeRanges].sort((a, b) => a.startTime.localeCompare(b.startTime));

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-date-picker-color', primaryColor);
        }
        if (primaryTextColor) {
            document.documentElement.style.setProperty('--primary-text-color', primaryTextColor);
        }
    }, [primaryColor, primaryTextColor]);

    const onSelectTimezone = (timezone: string) => {
        setSelectedTimezone(timezone);
    };

    const timeRangesByDay = sortedTimeRanges.reduce((timeRangesByDay, timeRangeResult) => {
        const timeRangeDay = DateTime.fromISO(timeRangeResult.startTime).toLocaleString(DateTime.DATE_SHORT);

        if (!timeRangesByDay.has(timeRangeDay)) {
            timeRangesByDay.set(timeRangeDay, []);
        }

        timeRangesByDay.get(timeRangeDay)?.push(timeRangeResult);

        return timeRangesByDay;
    }, new Map<string, TimeRange[]>());

    timeRangesByDay.forEach((timeRanges, key) => {
        if (!newDates.has(key)) {
            newDates.set(key, []);
        }

        timeRanges.forEach((timeRange) => {
            const totalMill = timeRange.durationMin * 60000;
            const duration = Duration.fromMillis(totalMill).shiftTo('hours', 'minutes');

            const itinerary = mapTimeRangeToItinerary(
                DateTime.fromISO(timeRange.startTime, { zone: selectedTimezone }),
                DateTime.fromISO(timeRange.endTime, { zone: selectedTimezone }),
                duration,
            );

            newDates.get(key)?.push(itinerary);
        });
    });

    return (
        <>
            <div className="listTimezonesContainer noMarginBottom">
                <ListTimezones
                    selectedValue={{
                        value: selectedTimezone,
                        label: '',
                    }}
                    onChange={(val) => onSelectTimezone(val)}
                    options={timeZoneList}
                    isDisabled={interview?.settings.isOnsite ?? false}
                />
            </div>
            {[...newDates.keys()].map((key: string, index: number) => {
                const dates = newDates.get(key);
                return (
                    <div key={key} className="itineraryCardContainer">
                        <div className="itineraryItem">
                            <div className="dateTimeContainer">
                                <div className="dateContainer">
                                    <span className="dayOfWeek">{dates?.[0]?.dayOfWeek}</span>
                                    <span className="dayOfMonth">{dates?.[0]?.dayOfMonth}</span>
                                    <span className="monthOfYear">{dates?.[0]?.monthOfYear}</span>
                                </div>
                                <div className="timeContainer">
                                    <div className="fullDate">
                                        <div className="day">{translate('general:day', { count: index + 1 })}</div>
                                        <span className="date">{dates?.[0]?.bookDate}</span>
                                    </div>
                                    {dates?.map((date, idx) => {
                                        return (
                                            <div key={idx} className="rangeTime">
                                                <span className="time">{date.bookTime}</span>
                                                <span className="durationTime">{date.duration}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default ItineraryCalendarDisplay;
